import {
  Box,
  Card,
  CardContent,
  CardMedia,
  DialogContent,
  Divider,
  Paper,
  Stack,
  Typography
} from '@mui/material'

import { Table, TableColumnsType, TableProps } from 'antd'
import { useTranslation } from 'react-i18next'

/**
 * 优化后的 CNV 嵌合/嵌合体弹窗内容
 *
 * 设计思路（从基因分析师视角）：
 * 1. 把“Result / Evidence Grade / Status”从 Chip 改成摘要卡，层级更清晰，避免 Chip 像可筛选标签的误导。
 * 2. 为表格和图表增加明确的分节标题，便于快速定位关键信息。
 * 3. 图片使用带标题的卡片包裹，增加边界感，避免图表紧贴边框难以辨识。
 * 4. 数值展示建议：Cell Fraction 转为百分比并保留 2 位小数，Alleleic Imbalance 保留 4 位小数。
 * 5. 保持足够留白（spacing={3}），让大量数据不显得拥挤。
 * 6. 对缺失值做兜底显示“-”，避免空字符串造成界面抖动。
 */

// 数值格式化辅助函数（可按项目精度要求调整）
const formatPercent = (value?: number | string | null, digits = 2) => {
  if (value === undefined || value === null || value === '') return '-'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(num)) return String(value)
  return `${(num * 100).toFixed(digits)}%`
}

const formatDecimal = (value?: number | string | null, digits = 4) => {
  if (value === undefined || value === null || value === '') return '-'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(num)) return String(value)
  return num.toFixed(digits)
}

// 示例：建议的 MosaicColumns 列配置（如果你已有列定义，可把 render 逻辑迁移过去）
// const MosaicColumns = [
//   { title: '位点', dataIndex: 'locus', key: 'locus' },
//   { title: 'Scale', dataIndex: 'scale', key: 'scale' },
//   { title: 'Finding', dataIndex: 'finding', key: 'finding' },
//   { title: 'Grade', dataIndex: 'grade', key: 'grade' },
//   {
//     title: 'Cell Fraction',
//     dataIndex: 'cell_fraction',
//     key: 'cell_fraction',
//     render: (v: number) => formatPercent(v)
//   },
//   {
//     title: 'Alleleic Imbalance',
//     dataIndex: 'alleleic_imbalance',
//     key: 'alleleic_imbalance',
//     render: (v: number) => formatDecimal(v)
//   },
//   { title: 'Informative sites', dataIndex: 'informative_sites', key: 'informative_sites' },
//   { title: 'Independent loci', dataIndex: 'independent_loci', key: 'independent_loci' }
// ]

const SummaryItem = ({
  label,
  value,
  color = 'text.primary'
}: {
  label: string
  value?: string | number
  color?: string
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>

    <Typography variant="h6" fontWeight={600} color={color}>
      {value ?? '-'}
    </Typography>
  </Box>
)

const ImageCard = ({
  src,
  title,
  alt
}: {
  src: string
  title: string
  alt: string
}) => (
  <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
    <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
    </CardContent>

    <CardMedia
      component="img"
      image={src}
      alt={alt}
      sx={{
        width: '100%',
        display: 'block',
        bgcolor: '#fff'
      }}
    />
  </Card>
)

const CnvMosaicDialogContent = ({
  analysisInfo,
  CnvMosaic,
  CnvChimerismMosaism,
  CnvChimerismMosaismPng,
  MosaicColumns,
  CnvChimerismMosaismColumns
}: {
  analysisInfo: { pipelineVersion?: string }
  CnvMosaic: {
    verdict?: string
    evidence_grade?: string
    status?: { state?: string }
    findings?: any[]
    plot_chromosomes_url?: string
    plot_baf_url?: string
    plot_findings_url?: string
  }
  CnvChimerismMosaism?: any[]
  CnvChimerismMosaismPng?: string
  MosaicColumns: any[]
  CnvChimerismMosaismColumns: any[]
}) => {
  const { t } = useTranslation()

  return (
    <DialogContent dividers>
      <Box sx={{ minHeight: 300, py: 1 }}>
        {analysisInfo.pipelineVersion ? (
          <Stack direction="column" spacing={3}>
            {/* 关键结果摘要 */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 4 }}
                divider={
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                  />
                }
              >
                <SummaryItem
                  label={t('Result')}
                  value={CnvMosaic.verdict}
                  color="primary.main"
                />

                <SummaryItem
                  label={t('Evidence Grade')}
                  value={CnvMosaic.evidence_grade}
                />

                <SummaryItem
                  label={t('Status')}
                  value={CnvMosaic.status?.state}
                />
              </Stack>
            </Paper>

            {/* 发现列表 */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('Mosaic Findings')}
              </Typography>

              <Table
                columns={MosaicColumns}
                dataSource={CnvMosaic.findings ?? []}
                pagination={false}
                size="small"
                bordered
              />
            </Box>

            {/* 基因组可视化 */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('Genomic Visualizations')}
              </Typography>

              <Stack spacing={2}>
                {CnvMosaic.plot_chromosomes_url && (
                  <ImageCard
                    src={CnvMosaic.plot_chromosomes_url}
                    title="Chromosome Overview"
                    alt="Chromosome overview"
                  />
                )}

                {CnvMosaic.plot_baf_url && (
                  <ImageCard
                    src={CnvMosaic.plot_baf_url}
                    title="B Allele Frequency (BAF)"
                    alt="BAF plot"
                  />
                )}

                {CnvMosaic.plot_findings_url && (
                  <ImageCard
                    src={CnvMosaic.plot_findings_url}
                    title="Reportable Findings"
                    alt="Findings plot"
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Stack direction="column" spacing={3}>
            {/* 新 pipeline 版本摘要 */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}
            >
              <SummaryItem
                label="Analysis Pipeline"
                value={`v${analysisInfo.pipelineVersion}`}
                color="primary.main"
              />
            </Paper>

            {/* 结果表格 */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Chimerism / Mosaicism Results
              </Typography>

              <Table
                columns={CnvChimerismMosaismColumns}
                dataSource={CnvChimerismMosaism ?? []}
                pagination={false}
                size="small"
                bordered
              />
            </Box>

            {/* 可视化图片 */}
            {CnvChimerismMosaismPng && (
              <ImageCard
                src={CnvChimerismMosaismPng}
                title="Chimerism / Mosaicism Visualization"
                alt="Chimerism mosaicism visualization"
              />
            )}
          </Stack>
        )}
      </Box>
    </DialogContent>
  )
}

export default CnvMosaicDialogContent

export { formatPercent, formatDecimal }
